import { createContext, ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { getActionsFromTranscript, mapAssistantActionsToVoice } from '../api/assistant';
import { MAX_RECORDING_SECONDS, transcribeAudio } from '../api/transcribe';
import { VoiceModal } from '../components/voice/VoiceModal';
import { i18next } from '../i18n';
import { VOICE_KEYFRAMES } from '../helpers/voiceKeyframes';
import { extractWaveform, formatVoiceError, getVoiceMimeType } from '../helpers/voice';
import { useVoiceActions } from '../hooks/useVoiceActions';
import { VoiceAction, VoiceActionUpdate, VoiceState, VoiceStateType } from '../types/voice';
import { AnalyticsEventType } from './analyticsEvents';
import { useAnalytics } from '../hooks/useAnalytics';

function collectRemovedActionIds(actions: VoiceAction[], removedId: string): Set<string> {
  const removed = new Set<string>([removedId]);
  const queue = [removedId];

  while (queue.length > 0) {
    const id = queue.pop()!;
    for (const action of actions) {
      if (removed.has(action.id)) continue;
      if (action.dependsOnActionIds?.includes(id)) {
        removed.add(action.id);
        queue.push(action.id);
      }
    }
  }

  return removed;
}

interface VoiceContextValue {
  voiceState: VoiceStateType;
  transcript: string;
  pendingActions: VoiceAction[];
  notification: string | null;
  voiceError: string | null;
  startVoice: () => void;
  cancel: () => void;
  retryTranscription: () => void;
  onRemoveAction: (id: string) => void;
  onUpdateVoiceAction: (id: string, data: VoiceActionUpdate) => void;
  onConfirmAll: () => void;
  isVoiceButtonDisabled: boolean;
  isRecording: boolean;
  recordingElapsedSeconds: number;
  recordingSessionId: number;
  recordedAudioUrl: string | null;
  recordedWaveform: number[];
  clearRecordedAudio: () => void;
}

const VoiceContext = createContext<VoiceContextValue | null>(null);

const MIC_GRANTED_KEY = 'voice_mic_granted';

export const VoiceProvider = ({ children }: { children: ReactNode }) => {
  const { execute } = useVoiceActions();
  const { recordEvent } = useAnalytics();
  const [voiceState, setVoiceState] = useState<VoiceStateType>(VoiceState.IDLE);
  const [transcript, setTranscript] = useState('');
  const [pendingActions, setPendingActions] = useState<VoiceAction[]>([]);
  const [notification, setNotification] = useState<string | null>(null);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingElapsedSeconds, setRecordingElapsedSeconds] = useState(0);
  const [recordingSessionId, setRecordingSessionId] = useState(0);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(null);
  const recordingStartRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [recordedWaveform, setRecordedWaveform] = useState<number[]>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeTypeRef = useRef<string>('audio/webm');
  const cancelledRef = useRef(false);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  const releaseWakeLock = useCallback(async () => {
    try {
      await wakeLockRef.current?.release();
      wakeLockRef.current = null;
    } catch {
      wakeLockRef.current = null;
    }
  }, []);

  const isVoiceButtonDisabled =
    voiceState === VoiceState.PROCESSING ||
    voiceState === VoiceState.SUCCESS ||
    voiceState === VoiceState.REVIEW ||
    (pendingActions.length > 0 && voiceState === VoiceState.IDLE);

  const clearRecordedAudio = useCallback(() => {
    setRecordedAudioUrl((prev) => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });
    setRecordedWaveform([]);
  }, []);

  const finishRecording = useCallback((): Blob | null => {
    if (chunksRef.current.length === 0) return null;
    const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current });
    const url = URL.createObjectURL(blob);
    setRecordedAudioUrl((prev) => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
      return url;
    });
    extractWaveform(blob).then(setRecordedWaveform);
    chunksRef.current = [];
    return blob;
  }, []);

  const processRecording = useCallback(
    async (blob: Blob) => {
      if (cancelledRef.current) return;
      setVoiceError(null);
      setVoiceState(VoiceState.PROCESSING);
      try {
        const language = i18next.language;
        const { text } = await transcribeAudio(blob, language);
        if (cancelledRef.current) return;
        setTranscript(text || '(no speech detected)');

        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const assistantActions = await getActionsFromTranscript(text, timezone, language);
        if (cancelledRef.current) return;
        const actions = mapAssistantActionsToVoice(assistantActions, timezone);

        if (cancelledRef.current) return;
        setPendingActions(actions);
        setVoiceState(VoiceState.REVIEW);
        recordEvent(AnalyticsEventType.VOICE_COMMAND_SUCCEEDED, {
          command_type: 'voice_assistant',
          action_count: actions.length,
        });
      } catch (e) {
        if (cancelledRef.current) return;
        setVoiceError(formatVoiceError(e));
        setVoiceState(VoiceState.ERROR);
        recordEvent(AnalyticsEventType.VOICE_COMMAND_FAILED, {
          command_type: 'voice_assistant',
          error_code: e instanceof Error ? e.message : 'unknown',
        });
      } finally {
        releaseWakeLock();
      }
    },
    [recordEvent, releaseWakeLock]
  );

  const stopStream = useCallback(() => {
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const stopRecordingSession = useCallback(
    (
      onRecorderStopped: () => void,
      releaseWakeLockIfNoActiveRecorder: boolean,
    ) => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      recordingStartRef.current = null;
      setRecordingElapsedSeconds(0);

      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.onstop = onRecorderStopped;
        recorder.stop();
      } else if (releaseWakeLockIfNoActiveRecorder) {
        void releaseWakeLock();
      }

      mediaRecorderRef.current = null;
      stopStream();
      setIsRecording(false);
    },
    [stopStream, releaseWakeLock],
  );

  const stopRecording = useCallback(() => {
    stopRecordingSession(
      () => {
        const blob = finishRecording();
        if (blob) void processRecording(blob);
        else void releaseWakeLock();
      },
      false,
    );
  }, [stopRecordingSession, finishRecording, processRecording, releaseWakeLock]);

  const startVoice = useCallback(async () => {
    if (voiceState === VoiceState.LISTENING && isRecording) {
      stopRecording();
      return;
    }

    if (typeof navigator !== 'undefined' && navigator.mediaDevices?.getUserMedia) {
      try {
        let stream = streamRef.current;
        const isStreamLive = stream?.active && stream.getTracks().every((t) => t.readyState === 'live');
        if (!stream || !isStreamLive) {
          stopStream();
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          streamRef.current = stream;
          localStorage.setItem(MIC_GRANTED_KEY, '1');
        }
        chunksRef.current = [];
        clearRecordedAudio();
        const mimeType = getVoiceMimeType();
        const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 64000 });
        mediaRecorderRef.current = recorder;
        mimeTypeRef.current = recorder.mimeType || mimeType;
        recorder.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
        recorder.start(250);
        if ('wakeLock' in navigator) {
          try {
            wakeLockRef.current = await navigator.wakeLock.request('screen');
          } catch {
            /* ignore */
          }
        }
        recordingStartRef.current = Date.now();
        setRecordingElapsedSeconds(0);
        timerRef.current = setInterval(() => {
          const elapsed = Math.floor((Date.now() - (recordingStartRef.current ?? 0)) / 1000);
          setRecordingElapsedSeconds(Math.min(elapsed, MAX_RECORDING_SECONDS));
          if (elapsed >= MAX_RECORDING_SECONDS) {
            if (timerRef.current) clearInterval(timerRef.current);
            timerRef.current = null;
            stopRecording();
          }
        }, 1000);
        setRecordingSessionId((s) => s + 1);
        setIsRecording(true);
      } catch {
        setVoiceError('Microphone access is required for voice commands');
        setVoiceState(VoiceState.ERROR);
        recordEvent(AnalyticsEventType.VOICE_COMMAND_FAILED, {
          command_type: 'voice_assistant',
          error_code: 'microphone_denied',
        });
        return;
      }
    }

    if (voiceState !== VoiceState.IDLE) return;
    cancelledRef.current = false;
    setTranscript('');
    setPendingActions([]);
    setVoiceState(VoiceState.LISTENING);
    recordEvent(AnalyticsEventType.VOICE_COMMAND_STARTED, { command_type: 'voice_assistant' });
  }, [voiceState, isRecording, stopRecording, stopStream, clearRecordedAudio, recordEvent]);

  const retryTranscription = useCallback(async () => {
    const url = recordedAudioUrl;
    if (!url) return;
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      await processRecording(blob);
    } catch {
      setVoiceError('Failed to retry');
    }
  }, [recordedAudioUrl, processRecording]);

  const discardActiveRecording = useCallback(() => {
    cancelledRef.current = true;
    stopRecordingSession(
      () => {
        chunksRef.current = [];
        void releaseWakeLock();
      },
      true,
    );
  }, [stopRecordingSession, releaseWakeLock]);

  const cancel = useCallback(() => {
    if (isRecording) discardActiveRecording();
    else {
      cancelledRef.current = true;
      stopStream();
      void releaseWakeLock();
    }
    setVoiceError(null);
    clearRecordedAudio();
    setVoiceState(VoiceState.IDLE);
    setPendingActions([]);
  }, [isRecording, discardActiveRecording, stopStream, clearRecordedAudio, releaseWakeLock]);

  const onRemoveAction = useCallback((id: string) => {
    setPendingActions((prev) => {
      const removedIds = collectRemovedActionIds(prev, id);
      return prev.filter((action) => !removedIds.has(action.id));
    });
  }, []);

  const onUpdateVoiceAction = useCallback((id: string, data: VoiceActionUpdate) => {
    setPendingActions((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...data } as VoiceAction : a))
    );
  }, []);

  const onConfirmAll = useCallback(async () => {
    const { success, errors } = await execute(pendingActions);
    const msg =
      errors.length > 0
        ? `${success} applied, ${errors.length} failed${errors[0] ? `: ${errors[0]}` : ''}`
        : `${success} action${success !== 1 ? 's' : ''} applied`;
    setNotification(msg);
    setVoiceState(VoiceState.SUCCESS);
    setTimeout(() => {
      clearRecordedAudio();
      setPendingActions([]);
      setVoiceState(VoiceState.IDLE);
      setNotification(null);
    }, 1300);
  }, [pendingActions, execute, clearRecordedAudio]);

  const value: VoiceContextValue = {
    voiceState,
    transcript,
    pendingActions,
    notification,
    voiceError,
    startVoice,
    cancel,
    retryTranscription,
    onRemoveAction,
    onUpdateVoiceAction,
    onConfirmAll,
    isVoiceButtonDisabled,
    isRecording,
    recordingElapsedSeconds,
    recordingSessionId,
    recordedAudioUrl,
    recordedWaveform,
    clearRecordedAudio,
  };

  return (
    <VoiceContext.Provider value={value}>
      <style>{VOICE_KEYFRAMES}</style>
      {children}
      <VoiceModal />
    </VoiceContext.Provider>
  );
};

export const useVoice = () => {
  const ctx = useContext(VoiceContext);
  if (!ctx) throw new Error('useVoice must be used within VoiceProvider');
  return ctx;
};
